import React, { useState, useRef, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "@speedoc/design-assets/build/icons";
import { Circle } from "@speedoc/design-assets/build/icons";
import ContainerCard from "./containerCard";
import { SimpleTabs } from "./tabs";
import { useMediaQuery } from "react-responsive";
import { Button } from "@speedoc/design-assets";
import SectionHeading from "./sectionHeading";

const ExpandedCardButtons = ({ content, onClick, mobile }) => {
  const scrollToForm = () => {
    let form;
    try {
      form = document.querySelector("#contactFormChronicV2");
      const topPoint = form.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({ behavior: "smooth", left: 0, top: topPoint });
    } catch {
      return;
    }
  };

  return (
    <div className="cardExpandedBottomButtons">
      <div className="buttonsContainer">
        <div className="buttons">
          <div className="linkPrimary sm" arrow="right" onClick={scrollToForm}>
            <span>{mobile ? content.expandedButtonDetailsTitleMobile : content.expandedButtonDetailsTitle}</span>
          </div>
          <Link to={content.linkToExploreUrl}>
            <Button className="secondary sm">
              <span>{content.linkToExploreTitle}</span>
            </Button>
          </Link>
        </div>
      </div>
      <div className="collapseContainer" onClick={onClick}>
        <ArrowLeft className="arrow left" />
        <span>{content.expandedCollapse}</span>
      </div>
    </div>
  );
};

const ExpandedCardBottomInfo = ({ content, services }) => {
  return (
    <div className="expandedCardBottomInfo">
      {content.expandedPrice && (
        <p className="priceInfo">
          {content.expandedCurrency}
          <span className="amount"> {content.expandedPrice}</span>
          <span className="year"> /yr</span>
        </p>
      )}
      <div className="packageDetailsList">
        <p>{services && services[0].text}</p>
        <div className="horizontalList">
          {services.slice(1, -1).map(item => {
            return <p key={item.text}>{item.text}</p>;
          })}
        </div>
        <p>{services[services.length - 1].text}</p>
      </div>
    </div>
  );
};

const PackageDetails = ({ content, fade }) => {
  return (
    <div className={`packageDetails ${fade ? "fadeContent" : ""}`}>
      {content.map(service => {
        return (
          <div key={service.title} className="serviceListItem">
            <div className="serviceName">
              <Circle /> <p>{service.title}</p>
            </div>
            {service.entries ? (
              <div className="serviceDetails">
                {service.entries.map(entry => {
                  return <p key={entry.text}>{entry.text}</p>;
                })}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
};

const ScheduleHorizontal = ({ content, fade }) => {
  return (
    <div className={`scheduleHorizontal ${fade ? "fadeContent" : ""}`}>
      <div className="timeline">
        <div />
      </div>
      <div className="boarderTop" />
      <div className="calendar">
        {content.map((period, index) => {
          return (
            <div key={index} className="timeSpanBox" style={{ width: `${100 / content.length}%` }}>
              {period.treatments.map((treatment, i) => (
                <p key={i} className="serviceType">
                  {treatment.text}
                </p>
              ))}
              <div className="bulletPointBox">
                <p className={`time ${period.month ? "" : "hidden"}`}>+{period.month} mo</p>
                <p className="label"> Visit {index + 1}</p>
                <div className="bulletPoint" />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const ScheduleVertical = ({ content, fade }) => {
  const container = useRef();

  const contentLength = content.length - 1;
  const boxHeight = `0 0 ${Math.round((100 / contentLength) * 10) / 10}%`;
  return (
    <div ref={container} className={`scheduleVertical ${fade ? "fadeContent" : ""}`}>
      <div className="details">
        <div className="timelineBoxes">
          {content.slice(0, -1).map((info, index) => (
            <div className="period" key={index} style={{ flex: boxHeight }}>
              {info.treatments.map(treatment => (
                <p key={treatment.text}>{treatment.text}</p>
              ))}
              <div className="label">
                <div className="bulletPoint" />
                <div className="text">
                  <p className="month">
                    {info.month !== "" ? <span>+{info.month} mo.</span> : <span className="hidden">mo.</span>}
                  </p>
                  <p className="visit">VISIT {index + 1}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="lastBox">
          <p>{content[content.length - 1]?.treatments[0]?.text}</p>
          <div className="label">
            <div className="bulletPoint" />
            <div className="text">
              <p className="month">
                {content[content.length - 1].month !== "" ? (
                  <span>+{content[content.length - 1].month} mo.</span>
                ) : (
                  <span className="hidden">mo.</span>
                )}
              </p>
              <p className="visit">VISIT {content.length}</p>
            </div>
          </div>
        </div>
      </div>
      <div className="timeline" />
      <div className="labels" />
    </div>
  );
};

const ExpandedCard = ({ content, onClick, style, className }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [fade, setFade] = useState(false);

  const fadeContent = value => {
    return new Promise(resolve => {
      setTimeout(() => {
        setFade(!fade);
        resolve();
      }, 200);
    });
  };

  const changeContent = i => {
    setActiveTab(i);
    return new Promise(resolve => {
      setTimeout(resolve, 100);
    });
  };

  const switchTab = async i => {
    if (activeTab === i) return;
    await changeContent(i);
    await fadeContent(true);
  };

  const buttonDetails = content.expandedTabs.map((item, index) => {
    return {
      text: item.title,
      classes: `secondary sm ${activeTab === index ? "active" : ""}`
    };
  });

  return (
    <ContainerCard style={style} className={`fullCard ${className}`} onClickOutside={onClick}>
      <h1 className="title">{content.expandedTitle}</h1>
      <div className="cardContent">
        <SimpleTabs buttonDetails={buttonDetails} onClick={i => switchTab(i)} />

        {activeTab === 0 ? (
          <PackageDetails content={content.expandedList} fade={fade} />
        ) : (
          <ScheduleHorizontal content={content.schedule} fade={fade} />
        )}
        <ExpandedCardBottomInfo content={content} services={content.expandedIncludesList} />

        <div className="cardFooter">
          <ExpandedCardButtons content={content} onClick={onClick} />
        </div>
      </div>
      {content.expandedImage && (
        <img className="cardImage" src={content.expandedImage?.url} alt={content.expandedTitle} />
      )}
    </ContainerCard>
  );
};

const PreviewCard = ({ content, onClick, className }) => {
  return (
    <ContainerCard className={className}>
      <h2 className="title">{content.previewTitle}</h2>
      <div className="cardBody">
        {content.expandedPrice && (
          <p className="priceInfo">
            {content.previewCurrency}
            <span className="amount"> {content.previewPrice}</span>
            <span className="year"> /yr</span>
          </p>
        )}
        <div className="servicesListSmall">
          {content.previewIncludesList.map(item => (
            <p key={item.text}>{item.text}</p>
          ))}
        </div>
        <div className="cardButtons">
          <Button onClick={onClick} type="button" className="secondary sm">
            <span>{content.previewButtonTitle}</span>
          </Button>
          <Link to="/" className="buttonLink link sm" arrow="right">
            <span>{content.previewLinkTitle}</span>
          </Link>
        </div>
      </div>
      {content.previewImage && <img className="cardImage" src={content.previewImage?.url} alt={content.previewTitle} />}
    </ContainerCard>
  );
};

const MobileCard = ({ content, tabs, className, handleClick, index, open }) => {
  const [displayBody, setDisplayBody] = useState(false);
  const [hidePreview, setHidePreview] = useState(false);
  const [fadeBody, setFadeBody] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [fade, setFade] = useState(false);

  const fadeContent = value => {
    return new Promise(resolve => {
      setTimeout(() => {
        setFade(!fade);
        resolve();
      }, 200);
    });
  };
  const changeContent = i => {
    setActiveTab(i);
    return new Promise(resolve => {
      setTimeout(resolve, 100);
    });
  };
  let dataAttrs = {
    "data-card": `card-${index}`
  };
  const buttonDetails = content.expandedTabs.map((item, tabIndex) => {
    return {
      text: item.title,
      classes: `secondary sm ${activeTab === tabIndex ? "active" : ""}`
    };
  });

  const switchTab = async i => {
    if (activeTab === i) return;
    await changeContent(i);
    await fadeContent(true);
  };

  const closeCard = e => {
    setFadeBody(prevState => !prevState);
    setTimeout(() => {
      showBody(350).then(() => {
        handleClick(e, index);
      });
    }, 200);
  };

  const showBody = useCallback((delay = 900) => {
    setHidePreview(prevState => !prevState);
    return new Promise(resolve => {
      setTimeout(() => {
        setDisplayBody(prevState => !prevState);
        resolve();
      }, delay);
    });
  }, []);

  useEffect(() => {
    if (open) {
      showBody().then(() =>
        setTimeout(() => {
          setFadeBody(prevState => !prevState);
        }, 200)
      );
    }
  }, [open, showBody]);

  return (
    <ContainerCard
      dataAttrs={dataAttrs}
      className={`mobileCard ${className}  ${open ? "expand" : ""} ${activeTab === 1 ? "noPaddingBody" : ""}`}>
      <h2 className="title">{content.expandedTitle}</h2>
      {displayBody ? (
        <div className={`cardBody ${fadeBody ? "display" : ""}`}>
          <SimpleTabs buttonDetails={buttonDetails} onClick={i => switchTab(i)} />
          {activeTab === 0 ? (
            <PackageDetails content={content.expandedList} fade={fade} />
          ) : (
            <ScheduleVertical content={content.schedule} fade={fade} />
          )}
          <ExpandedCardBottomInfo content={content} services={content.expandedIncludesListMobile} />
          <ExpandedCardButtons content={content} onClick={closeCard} mobile={true} />
        </div>
      ) : (
        <div className={`cardPreview ${hidePreview ? "hide" : ""}`}>
          <div className="cardDetails">
            {content.expandedPrice && (
              <p className="priceInfo">
                {content.expandedCurrency}
                <span className="amount"> {content.expandedPrice}</span>
                <span className="year"> /yr</span>
              </p>
            )}
          </div>
          <div className="servicesListSmall">
            {content.expandedIncludesListMobile.map(item => (
              <p key={item.text}>{item.text}</p>
            ))}
          </div>
          <div>
            <Button onClick={handleClick} type="button" className="secondary sm">
              <span>{content.expandedButtonDetailsTitle}</span>
            </Button>
          </div>
        </div>
      )}
      <img className="cardImage" src={content.expandedImage?.url} alt={content.expandedTitle} />
    </ContainerCard>
  );
};

const ChronicPackagesDetails = ({ content }) => {
  const [activeTab] = useState("Basic");
  const [blockClick, setBlockClick] = useState(false);
  const [expandCardIndex, setExpandCardIndex] = useState();
  const [movePreviewCard, setMovePreviewCard] = useState(undefined);
  const [displayFullCard, setDisplayFullCard] = useState(false);
  const [expandFullCard, setExpandFullCard] = useState(false);
  const [showCardContent, setShowCardContent] = useState(false);
  const [fullContainerStyle, setFullContainerStyle] = useState({});
  const [fullCardStyle, setFullCardStyle] = useState({});
  const previewContainer = useRef();

  const injectCard = (delay = 300) => {
    return new Promise(resolve => {
      setDisplayFullCard(!displayFullCard);
      setTimeout(resolve, delay);
    });
  };

  const expandCard = (delay = 400) => {
    return new Promise(resolve => {
      setMovePreviewCard(undefined);
      setExpandFullCard(!expandFullCard);
      setTimeout(resolve, delay);
    });
  };

  const showContent = (delay = 800) => {
    return new Promise(resolve => {
      setShowCardContent(!showCardContent);
      setTimeout(resolve, delay);
    });
  };

  const closeCard = () => {
    if (blockClick) return;
    setBlockClick(true);
    showContent(450)
      .then(() => expandCard())
      .then(() => {
        injectCard(100);
      })
      .then(() =>
        setTimeout(() => {
          setExpandCardIndex(undefined);
          setBlockClick(false);
        }, 100)
      );
  };

  const startAnimation = (e, index) => {
    if (blockClick) return;
    setBlockClick(true);
    setExpandCardIndex(index);
    setMovePreviewCard(index);
    const targetCard = e.currentTarget.parentElement.parentElement.parentElement;
    setFullCardStyle({
      position: "relative",
      width: targetCard.offsetWidth,
      height: targetCard.offsetHeight
    });
    setFullContainerStyle({
      paddingTop: targetCard.offsetTop,
      paddingBottom: index < 2 ? previewContainer.current.offsetHeight - targetCard.offsetHeight : 0,
      paddingLeft: targetCard.offsetLeft,
      paddingRight: index % 2 === 0 ? previewContainer.current.offsetWidth - targetCard.offsetWidth : 0,
      justifyContent: index % 2 === 1 ? "flex-end" : "flex-start",
      alignItems: index > 1 ? "flex-end" : "flex-start"
    });
    setTimeout(() => {
      injectCard(10)
        .then(expandCard)
        .then(() => showContent(250))
        .then(() => setBlockClick(false));
    }, 700);
  };

  const smallCardTransition = index => (movePreviewCard === index ? `translate-${index}` : "");

  return (
    <div id="chronicPackagesDetails">
      <div class="previewCardsWrapper">
        <SectionHeading title={content.sectionTitle} />
        <div className="tabsContainer">
          {content.tabs.map(item => (
            <p key={item.title} className={item.title === activeTab ? "active" : ""}>
              {item.title}
            </p>
          ))}
        </div>
        <div ref={previewContainer} className="previewCardsContainer">
          {content.tabCards.map((cardContent, index) => (
            <PreviewCard
              key={index}
              className={`smallCard ${expandCardIndex === index ? "fadeContent" : ""}  ${
                movePreviewCard !== undefined ? smallCardTransition(index) : ""
              }`}
              content={cardContent}
              onClick={e => startAnimation(e, index)}
            />
          ))}
          {displayFullCard ? (
            <div className={`expandedCardContainer ${expandFullCard ? "expand" : ""}`} style={fullContainerStyle}>
              <ExpandedCard
                className={`offset-${movePreviewCard} ${showCardContent ? "" : "transparent"}`}
                style={fullCardStyle}
                content={content.tabCards[expandCardIndex]}
                onClick={closeCard}
              />
            </div>
          ) : null}
        </div>
        <p className="bottomLine">{content.bottomLine}</p>
      </div>
    </div>
  );
};

const ChronicPackagesDetailsMobile = ({ content }) => {
  const [activeTab] = useState("Basic");
  const [openCardIndexes, setOpenCardIndexes] = useState([]);
  const container = useRef();

  const isSafari = () => {
    return (
      navigator.vendor &&
      navigator.vendor.indexOf("Apple") > -1 &&
      navigator.userAgent &&
      navigator.userAgent.indexOf("CriOS") === -1 &&
      navigator.userAgent.indexOf("FxiOS") === -1
    );
  };

  function scrollIosScreen(position, time = 250) {
    return new Promise(resolve => {
      var scrollY = position / 50;
      var curTime = 0;
      while (curTime <= time) {
        window.setTimeout(() => {
          window.scrollBy({ left: 0, top: scrollY });
        }, curTime);
        curTime += time / 50;
      }
      resolve();
    });
  }

  const scrollScreen = position => {
    window.scrollBy(0, position);
    return new Promise(resolve => {
      setTimeout(resolve, 300);
    });
  };

  const updateIndexes = index => {
    setOpenCardIndexes([...openCardIndexes, index]);
    return new Promise(resolve => {
      setTimeout(resolve, 300);
    });
  };

  const safariScrollPosition = position => {
    const navBarHeight = document.querySelector("#navigationMobile").offsetHeight;
    if (position < 1) {
      return -(-position + navBarHeight);
    } else if (position <= navBarHeight) {
      return -(position + navBarHeight);
    } else {
      return position - navBarHeight;
    }
  };
  const openCard = async (position, index) => {
    if (isSafari()) {
      scrollIosScreen(safariScrollPosition(position), 450, "top").then(() =>
        setTimeout(() => {
          updateIndexes(index);
        }, 400)
      );
    } else {
      scrollScreen(position - 99).then(() => updateIndexes(index));
    }
  };

  const handleClick = async (e, index) => {
    const card = container.current.querySelector(`[data-card='card-${index}']`);
    const cardTop = card.getBoundingClientRect().top;
    if (openCardIndexes.includes(index)) {
      setOpenCardIndexes(openCardIndexes.filter(i => i !== index));
      if (isSafari()) {
        scrollIosScreen(safariScrollPosition(cardTop), 450, "top");
      } else {
        scrollScreen(cardTop - 99);
      }
      return;
    }
    openCard(cardTop, index);
  };

  return (
    <div id="chronicPackagesDetailsMobile">
      <SectionHeading title={content.sectionTitle} />
      <div className="tabsContainer">
        {content.tabs.map(item => (
          <p key={item.title} className={item.title === activeTab ? "active" : ""}>
            {item.title}
          </p>
        ))}
      </div>
      <div ref={container} className="cardsContainer">
        {content.tabCards.map((cardContent, index) => (
          <MobileCard
            key={index}
            index={index}
            handleClick={e => handleClick(e, index)}
            open={openCardIndexes.includes(index)}
            className="card"
            content={cardContent}
          />
        ))}
      </div>
    </div>
  );
};

export default ({ content }) => {
  const isMobile = useMediaQuery({ query: "(max-width: 992px)" });
  if (isMobile) {
    return <ChronicPackagesDetailsMobile content={content} />;
  } else {
    return <ChronicPackagesDetails content={content} />;
  }
};
